/**
 * Module dependencies.
 */

import {
  ArrowUpFromLine,
  FileText,
  FolderInput,
  MoreHorizontal,
  NotebookText,
  Pencil,
  RotateCcw,
  Star,
  Trash2
} from 'lucide-react';
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { NoteFile } from 'app/hooks/requests/apps-notes/use-get-notes-file-system';
import { NoteSearchResult } from 'app/hooks/requests/apps-notes/use-notes-search';
import { NotesSelection } from './types';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { Spinner } from 'app/components/ui/spinner';
import { useActionsContent } from './actions-provider';
import { useNotesTrash, usePurgeItem, useRestoreItem } from 'app/hooks/requests/apps-notes/use-notes-trash';
import { usePostNotesEditItem } from 'app/hooks/requests/apps-notes/use-post-edit-item';
import { usePostSortNotes } from 'app/hooks/requests/apps-notes/use-post-sort-notes';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import { cn } from 'app/lib/utils';

/**
 * Compact relative time (e.g. "3h ago", "2d ago", "Aug 4").
 */

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();

  if (Number.isNaN(then)) {
    return '';
  }

  const seconds = Math.round((Date.now() - then) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 60) {
    return 'just now';
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  if (hours < 24) {
    return `${hours}h ago`;
  }

  if (days < 7) {
    return `${days}d ago`;
  }

  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/**
 * Emphasize occurrences of `term` within `text`.
 */

function Highlight({ term, text }: { term: string; text: string }): JSX.Element {
  const trimmed = term.trim();

  if (!trimmed) {
    return <>{text}</>;
  }

  const parts = text.split(new RegExp(`(${trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === trimmed.toLowerCase() ? (
          <mark className={'rounded bg-primary/20 text-foreground'} key={index}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

/**
 * `RenameDialog` component.
 */

function RenameDialog({
  initial,
  onClose,
  onSubmit,
  open
}: {
  initial: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
  open: boolean;
}): JSX.Element {
  const [translate] = useTranslation();
  const [value, setValue] = useState(initial);

  React.useEffect(() => {
    if (open) {
      setValue(initial);
    }
  }, [initial, open]);

  return (
    <Dialog onOpenChange={next => !next && onClose()} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('notes.actions.rename')}</DialogTitle>
        </DialogHeader>

        <input
          autoFocus
          className={
            'h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
          }
          onChange={event => setValue(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && value.trim()) {
              onSubmit(value.trim());
            }
          }}
          value={value}
        />

        <DialogFooter>
          <button
            className={'rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent'}
            onClick={onClose}
            type={'button'}
          >
            {translate('common.actions.cancel')}
          </button>

          <button
            className={
              'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50'
            }
            disabled={!value.trim()}
            onClick={() => value.trim() && onSubmit(value.trim())}
            type={'button'}
          >
            {translate('common.actions.save')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * `NoteCard` component — a note with a favorite star and a ⋯ actions menu.
 */

function NoteCard({
  folders,
  note,
  onOpen,
  selected
}: {
  folders: { key: string; name: string }[];
  note: NoteFile;
  onOpen: (key: string) => void;
  selected: boolean;
}): JSX.Element {
  const [translate] = useTranslation();
  const { error, success } = useToast();
  const { deleteItem, toggleFavorite } = useActionsContent();
  const { mutateAsync: editItem } = usePostNotesEditItem();
  const { mutateAsync: sortItems } = usePostSortNotes();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const name = typeof note.title === 'string' ? note.title : '';

  const onMove = async (parentId: string | null) => {
    setMenuOpen(false);

    try {
      await sortItems({ afterId: undefined, beforeId: undefined, itemId: note.key, newParentId: parentId });
      success({ message: translate('notes.card.moved') });
    } catch (err) {
      error({ message: translate('notes.errors.moveFailed') });
    }
  };

  const onDelete = async () => {
    setDeleteOpen(false);

    try {
      await deleteItem({ id: note.key });
      success({ message: translate('notes.movedToTrash') });
    } catch (err) {
      // Provider surfaces the error.
    }
  };

  const onRename = async (value: string) => {
    setRenameOpen(false);

    try {
      await editItem({ id: note.key, name: value });
    } catch (err) {
      error({ message: translate('notes.errors.renameFailed') });
    }
  };

  return (
    <div
      className={cn(
        'group flex items-start gap-2 rounded-lg border border-transparent px-3 py-2.5 transition-colors hover:bg-muted/60',
        selected && 'border-primary/15 bg-primary/5 hover:bg-primary/5'
      )}
    >
      <button
        className={'flex min-w-0 flex-1 flex-col gap-1.5 text-left'}
        onClick={() => onOpen(note.key)}
        type={'button'}
      >
        <div className={'flex items-center gap-2'}>
          <FileText className={'h-4 w-4 shrink-0 text-muted-foreground'} />

          <span className={'min-w-0 flex-1 truncate text-sm font-semibold'} title={name}>
            {name}
          </span>
        </div>

        {note.updatedAt && (
          <span className={'pl-6 text-[11px] tabular-nums text-muted-foreground'}>{relativeTime(note.updatedAt)}</span>
        )}
      </button>

      <div className={'flex shrink-0 items-center gap-0.5'}>
        <button
          className={cn(
            'rounded p-1 transition-opacity hover:text-foreground',
            note.favorite ? 'text-amber-500 opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100'
          )}
          onClick={() => toggleFavorite({ favorite: !note.favorite, id: note.key })}
          title={translate('notes.sections.favorites')}
          type={'button'}
        >
          <Star className={cn('h-3.5 w-3.5', note.favorite && 'fill-current')} />
        </button>

        <Popover onOpenChange={setMenuOpen} open={menuOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'rounded p-1 text-muted-foreground transition-opacity hover:text-foreground',
                menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              )}
              title={translate('notes.card.actions')}
              type={'button'}
            >
              <MoreHorizontal className={'h-4 w-4'} />
            </button>
          </PopoverTrigger>

          <PopoverContent align={'end'} className={'w-56 p-1'}>
            <button
              className={'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-accent'}
              onClick={() => {
                setMenuOpen(false);
                setRenameOpen(true);
              }}
              type={'button'}
            >
              <Pencil className={'h-4 w-4 text-muted-foreground'} />

              {translate('notes.actions.rename')}
            </button>

            <div className={'my-1 border-t border-border'} />

            <div className={'px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
              <span className={'inline-flex items-center gap-1.5'}>
                <FolderInput className={'h-3.5 w-3.5'} />

                {translate('notes.actions.move')}
              </span>
            </div>

            <div className={'max-h-48 overflow-y-auto'}>
              <button
                className={'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-accent'}
                onClick={() => onMove(null)}
                type={'button'}
              >
                <ArrowUpFromLine className={'h-4 w-4 text-muted-foreground'} />

                {translate('notes.actions.moveRoot')}
              </button>

              {folders.map(folder => (
                <button
                  className={
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-accent'
                  }
                  key={folder.key}
                  onClick={() => onMove(folder.key)}
                  type={'button'}
                >
                  <FileText className={'h-4 w-4 text-muted-foreground'} />

                  <span className={'min-w-0 flex-1 truncate'}>{folder.name}</span>
                </button>
              ))}
            </div>

            <div className={'my-1 border-t border-border'} />

            <button
              className={
                'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-destructive hover:bg-destructive/10'
              }
              onClick={() => {
                setMenuOpen(false);
                setDeleteOpen(true);
              }}
              type={'button'}
            >
              <Trash2 className={'h-4 w-4'} />

              {translate('notes.actions.delete')}
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <RenameDialog initial={name} onClose={() => setRenameOpen(false)} onSubmit={onRename} open={renameOpen} />

      <AlertDialog onOpenChange={setDeleteOpen} open={deleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('notes.actions.removeItem')}</AlertDialogTitle>

            <AlertDialogDescription>{translate('notes.removeItemDescription')}</AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              onClick={onDelete}
            >
              {translate('common.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * `TrashView` component — soft-deleted items with restore / delete-permanently.
 */

function TrashView(): JSX.Element {
  const [translate] = useTranslation();
  const { error, success } = useToast();
  const { data: items, isLoading } = useNotesTrash();
  const { mutateAsync: restore } = useRestoreItem();
  const { mutateAsync: purge } = usePurgeItem();

  if (isLoading) {
    return (
      <div className={'grid flex-1 place-items-center'}>
        <Spinner />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className={'flex flex-1 flex-col items-center justify-center px-6 text-center text-muted-foreground'}>
        <Trash2 className={'mb-2 h-8 w-8 opacity-40'} />

        <p className={'text-xs'}>{translate('notes.trash.empty')}</p>
      </div>
    );
  }

  return (
    <div className={'flex-1 space-y-0.5 overflow-y-auto px-2 pb-4'}>
      {items.map(item => (
        <div className={'group flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-muted/60'} key={item.id}>
          <FileText className={'h-4 w-4 shrink-0 text-muted-foreground'} />

          <span className={'min-w-0 flex-1 truncate text-sm'} title={item.name}>
            {item.name}
          </span>

          <button
            className={
              'rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100'
            }
            onClick={async () => {
              try {
                await restore({ id: item.id });
                success({ message: translate('notes.trash.restored') });
              } catch (err) {
                error({ message: translate('notes.errors.restoreFailed') });
              }
            }}
            title={translate('notes.trash.restore')}
            type={'button'}
          >
            <RotateCcw className={'h-4 w-4'} />
          </button>

          <button
            className={
              'rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100'
            }
            onClick={async () => {
              try {
                await purge({ id: item.id });
              } catch (err) {
                error({ message: translate('notes.errors.purgeFailed') });
              }
            }}
            title={translate('notes.trash.purge')}
            type={'button'}
          >
            <Trash2 className={'h-4 w-4'} />
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * `Props` type.
 */

type Props = {
  title: string;
  selection: NotesSelection;
  notes: NoteFile[];
  folders: { key: string; name: string }[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  query: string;
  searchActive: boolean;
  isSearching: boolean;
  searchResults: NoteSearchResult[] | undefined;
};

/**
 * Export `NoteList` component — the middle column: the notes for the active
 * selection (or search results / trash).
 */

export function NoteList({
  folders,
  isSearching,
  notes,
  onSelect,
  query,
  searchActive,
  searchResults,
  selectedKey,
  selection,
  title
}: Props): JSX.Element {
  const [translate] = useTranslation();

  const headerTitle = searchActive ? translate('notes.search.results') : title;
  const count = searchActive ? searchResults?.length ?? 0 : selection.type === 'trash' ? undefined : notes.length;

  return (
    <div className={'flex h-full flex-col'}>
      <div className={'flex items-center justify-between gap-2 px-4 pb-2 pt-4'}>
        <h2 className={'truncate text-[15px] font-bold'} title={headerTitle}>
          {headerTitle}
        </h2>

        {count !== undefined && (
          <span className={'shrink-0 text-[11px] tabular-nums text-muted-foreground'}>
            {translate('notes.list.count', { count })}
          </span>
        )}
      </div>

      {searchActive ? (
        isSearching && !searchResults ? (
          <div className={'grid flex-1 place-items-center'}>
            <Spinner />
          </div>
        ) : !searchResults || searchResults.length === 0 ? (
          <p className={'px-4 py-8 text-center text-sm text-muted-foreground'}>{translate('notes.search.empty')}</p>
        ) : (
          <div className={'flex-1 space-y-0.5 overflow-y-auto px-2 pb-4'}>
            {searchResults.map(result => (
              <button
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left hover:bg-muted/60',
                  selectedKey === result.id && 'bg-primary/5'
                )}
                key={result.id}
                onClick={() => onSelect(result.id)}
                type={'button'}
              >
                <span className={'truncate text-sm font-semibold'}>
                  <Highlight term={query} text={result.name} />
                </span>

                {result.path.length > 0 && (
                  <span className={'truncate text-[11px] text-muted-foreground'}>{result.path.join(' / ')}</span>
                )}

                {result.snippet && (
                  <span className={'line-clamp-2 text-xs text-muted-foreground'}>
                    <Highlight term={query} text={result.snippet} />
                  </span>
                )}
              </button>
            ))}
          </div>
        )
      ) : selection.type === 'trash' ? (
        <TrashView />
      ) : notes.length === 0 ? (
        <div className={'flex flex-1 flex-col items-center justify-center px-6 text-center text-muted-foreground'}>
          <NotebookText className={'mb-2 h-8 w-8 opacity-40'} />

          <p className={'text-xs'}>{translate('notes.list.empty')}</p>
        </div>
      ) : (
        <div className={'flex-1 space-y-0.5 overflow-y-auto px-2 pb-4'}>
          {notes.map(note => (
            <NoteCard
              folders={folders}
              key={note.key}
              note={note}
              onOpen={onSelect}
              selected={selectedKey === note.key}
            />
          ))}
        </div>
      )}
    </div>
  );
}
